// Frontend mirror of the canonical backend issue derivation. Computes the
// values that are DERIVED ON READ from an issue + its action dates and never
// stored: the action lifecycle state (open / done / overdue), how many whole
// days a done action missed its due date by, the issue's age, and how long
// it has sat in its current status.
//
// All inputs are YYYY-MM-DD Australia/Brisbane calendar days; differences are
// whole-day counts computed at the UTC day boundary so the result is never
// wall-clock or timezone dependent. The shared parity fixture
// (tests/shared/issueDerivation/issueActionState.json) is the binding
// contract — this module must produce the fixtures' expected outputs exactly
// (ADR-015). Any rule change here must update the fixture and keep BOTH the
// FE and BE parity suites green.

import type { IssueActionState } from "../../../../types";

const DAY_MS = 86_400_000;

/** Parse a YYYY-MM-DD calendar day to its UTC-midnight epoch ms. */
function dayMs(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`);
}

/** Whole calendar days from `from` to `to` (both YYYY-MM-DD). Negative when
 *  `to` precedes `from`; callers floor at 0 where a negative is meaningless. */
function wholeDaysBetween(from: string, to: string): number {
  return Math.round((dayMs(to) - dayMs(from)) / DAY_MS);
}

export interface DeriveIssueDisplayInput {
  /** Brisbane day the derivation is evaluated against (YYYY-MM-DD). */
  today: string;
  /** When the issue was raised (YYYY-MM-DD). */
  raisedAt: string;
  /** When the current status was entered (YYYY-MM-DD). */
  statusEnteredAt: string;
  /** Action due date (YYYY-MM-DD). Omitted = an action with no due date. */
  actionDueDate?: string;
  /** Action completion date (YYYY-MM-DD). Omitted = not yet done. */
  actionDoneAt?: string;
}

export interface DeriveIssueDisplayResult {
  /** Derived action lifecycle. `done` when completed; otherwise `overdue`
   *  when today is strictly past the due date; otherwise `open`. An action
   *  with no due date stays `open` until done. */
  actionState: IssueActionState;
  /** Whole days a done action missed its due date by; 0 when on-time, not
   *  done, or there is no due date. */
  daysLate: number;
  /** Whole days from raisedAt to today. */
  issueAgeDays: number;
  /** Whole days from statusEnteredAt to today — drives the row's
   *  duration-in-state indicator. */
  durationInStateDays: number;
}

/** Derive an action's lifecycle state. `done` overrides everything (an
 *  action completed long after its due date is still done, not overdue);
 *  `overdue` requires a due date strictly before today; otherwise `open`. */
export function deriveIssueActionState(
  today: string,
  actionDueDate?: string,
  actionDoneAt?: string,
): IssueActionState {
  if (actionDoneAt) return "done";
  if (actionDueDate && dayMs(today) > dayMs(actionDueDate)) return "overdue";
  return "open";
}

export function deriveIssueDisplay(
  input: DeriveIssueDisplayInput,
): DeriveIssueDisplayResult {
  const { today, raisedAt, statusEnteredAt, actionDueDate, actionDoneAt } =
    input;

  const actionState = deriveIssueActionState(today, actionDueDate, actionDoneAt);

  // Lateness is only meaningful for a done action with a due date; a
  // completion on or before the due date (or no due date) is zero days late.
  const daysLate =
    actionDoneAt && actionDueDate
      ? Math.max(0, wholeDaysBetween(actionDueDate, actionDoneAt))
      : 0;

  return {
    actionState,
    daysLate,
    issueAgeDays: wholeDaysBetween(raisedAt, today),
    durationInStateDays: wholeDaysBetween(statusEnteredAt, today),
  };
}
